import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
export { BACKEND_URL };

const TOKEN_KEY = 'pe_admin_token';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token && config.url && config.url.startsWith('/admin') && !config.url.startsWith('/admin/login')) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && err?.config?.url?.startsWith('/admin') && !err.config.url.startsWith('/admin/login')) {
      clearToken();
      if (window.location.pathname.startsWith('/admin')) {
        window.location.reload();
      }
    }
    return Promise.reject(err);
  }
);

export const publicApi = {
  getPackages: () => api.get('/packages').then(r => r.data),
  getReviews: () => api.get('/reviews').then(r => r.data),
  getStats: () => api.get('/stats').then(r => r.data),
  getAvailability: (date) => api.get('/availability', { params: { date } }).then(r => r.data),
  createReservation: (body) => api.post('/reservations', body).then(r => r.data),
  getReservation: (ref) => api.get(`/reservations/${ref}`).then(r => r.data),
  payMobileMoney: (body) => api.post('/payments/mobile-money', body).then(r => r.data),
  initFedaPay: (kind, reference, tiktok_consent) => api.post('/payments/init', { kind, reference, tiktok_consent }).then(r => r.data),
  verifyPayment: (reference) => api.get(`/payments/verify/${reference}`).then(r => r.data),
  pricingBreakdown: (kind, reference) => api.get(`/pricing/breakdown/${kind}/${reference}`).then(r => r.data),
  getContactSettings: () => api.get('/contact-settings').then(r => r.data),
  getProducts: () => api.get('/products').then(r => r.data),
  getProduct: (id) => api.get(`/products/${id}`).then(r => r.data),
  createOrder: (body) => api.post('/orders', body).then(r => r.data),
  payOrder: (body) => api.post('/orders/pay', body).then(r => r.data),
  invoiceUrl: (reference) => `${API}/orders/invoice/${reference}`,
  getSettings: () => api.get('/settings').then(r => r.data),
  getContactSettings: () => api.get('/contact-settings').then(r => r.data),
  getLagoonCards: () => api.get('/lagoon-cards').then(r => r.data),
  getLoyalty: (email) => api.get(`/loyalty/${encodeURIComponent(email)}`).then(r => r.data),
  getCustomerCoupons: (email) => api.get(`/coupons/customer/${encodeURIComponent(email)}`).then(r => r.data),
  validateCoupon: (code, email) => api.post('/coupons/validate', { code, email }).then(r => r.data),
  trackOrder: (reference) => api.get(`/orders/track/${reference}`).then(r => r.data)
};

export const adminApi = {
  login: (username, password) => api.post('/admin/login', { username, password }).then(r => r.data),
  me: () => api.get('/admin/me').then(r => r.data),
  changePassword: (current_password, new_password) => api.post('/admin/change-password', { current_password, new_password }).then(r => r.data),
  getDashboard: () => api.get('/admin/dashboard').then(r => r.data),
  getReservations: (status) => api.get('/admin/reservations', { params: status ? { status } : {} }).then(r => r.data),
  updateReservation: (id, body) => api.patch(`/admin/reservations/${id}`, body).then(r => r.data),
  getOrders: () => api.get('/admin/orders').then(r => r.data),
  updateOrderStatus: (id, body) => api.patch(`/admin/orders/${id}`, body).then(r => r.data),
  getCoupons: () => api.get('/admin/coupons').then(r => r.data),
  createCoupon: (body) => api.post('/admin/coupons', body).then(r => r.data),
  updateCoupon: (id, body) => api.patch(`/admin/coupons/${id}`, null, { params: body }).then(r => r.data),
  deleteCoupon: (id) => api.delete(`/admin/coupons/${id}`).then(r => r.data),
  getLoyaltyCustomers: () => api.get('/admin/loyalty').then(r => r.data),
  getReviews: () => api.get('/admin/reviews').then(r => r.data),
  createReview: (body) => api.post('/admin/reviews', body).then(r => r.data),
  updateReview: (id, body) => api.patch(`/admin/reviews/${id}`, body).then(r => r.data),
  deleteReview: (id) => api.delete(`/admin/reviews/${id}`).then(r => r.data),
  getPackages: () => api.get('/packages').then(r => r.data),
  createPackage: (body) => api.post('/admin/packages', body).then(r => r.data),
  updatePackage: (id, body) => api.patch(`/admin/packages/${id}`, body).then(r => r.data),
  deletePackage: (id) => api.delete(`/admin/packages/${id}`).then(r => r.data),
  getSettings: () => api.get('/settings').then(r => r.data),
  updateSettings: (body) => api.patch('/admin/settings', body).then(r => r.data),
  uploadHeroVideo: (file, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/admin/upload/hero-video', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => { if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100)); },
    }).then(r => r.data);
  },
  // Payment settings (super-admin only)
  getPaymentSettings: () => api.get('/admin/payment-settings').then(r => r.data),
  updatePaymentSettings: (body) => api.patch('/admin/payment-settings', body).then(r => r.data),
  // Contact settings
  getContactSettings: () => api.get('/contact-settings').then(r => r.data),
  updateContactSettings: (body) => api.patch('/admin/contact-settings', body).then(r => r.data),
  // Transactions
  listTransactions: (params = {}) => api.get('/admin/transactions', { params }).then(r => r.data),
  // Lagoon cards (super_admin)
  listLagoonCards: () => api.get('/lagoon-cards').then(r => r.data),
  createLagoonCard: (body) => api.post('/admin/lagoon-cards', body).then(r => r.data),
  updateLagoonCard: (id, body) => api.patch(`/admin/lagoon-cards/${id}`, body).then(r => r.data),
  deleteLagoonCard: (id) => api.delete(`/admin/lagoon-cards/${id}`).then(r => r.data),
  uploadLagoonVideo: (id, file, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/admin/lagoon-cards/${id}/upload-video`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => { if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100)); },
    }).then(r => r.data);
  },
  getEmails: () => api.get('/admin/emails').then(r => r.data),
  // Users
  getUsers: () => api.get('/admin/users').then(r => r.data),
  createUser: (body) => api.post('/admin/users', body).then(r => r.data),
  updateUser: (id, body) => api.patch(`/admin/users/${id}`, body).then(r => r.data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`).then(r => r.data),
  // Activity
  getActivity: (params = {}) => api.get('/admin/activity', { params }).then(r => r.data),
  // Notifications
  getNotifications: () => api.get('/admin/notifications').then(r => r.data),
  markNotificationsRead: (body = {}) => api.post('/admin/notifications/mark-read', body).then(r => r.data),
  // Charts
  getCharts: () => api.get('/admin/dashboard/charts').then(r => r.data),
  runReminders: () => api.post('/admin/reminders/run-now').then(r => r.data),
  // Products
  getProducts: () => api.get('/admin/products').then(r => r.data),
  createProduct: (body) => api.post('/admin/products', body).then(r => r.data),
  updateProduct: (id, body) => api.patch(`/admin/products/${id}`, body).then(r => r.data),
  deleteProduct: (id) => api.delete(`/admin/products/${id}`).then(r => r.data),
  // Exports (returns blob)
  exportReservationsXlsx: () => api.get('/admin/exports/reservations.xlsx', { responseType: 'blob' }).then(r => r.data),
  exportReportPdf: () => api.get('/admin/exports/report.pdf', { responseType: 'blob' }).then(r => r.data)
};

export default api;
